import React, { useState, useEffect } from "react";
import DocumentUpload from "../../components/DocumentUpload";
import LocationSelectors from "../../components/LocationSelectors";
import { useNavigate, useLocation } from "react-router-dom";
import "../../styles/FormPages.css";

const ApplicationFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { country, state: stateVal, district, lawCategory } = location.state || {};

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const [formData, setFormData] = useState({
    country: country || "",
    state: stateVal || "",
    district: district || "",

    applicantFullName: "",
    applicantFatherOrHusband: "",
    applicantAge: "",
    applicantGender: "",
    applicantOccupation: "",
    applicantContact: "",
    applicantAddress: "",
    applicantEmail: "",
    applicationType: "",
    applicationTypeOther: "",
    applicationTo: "",
    designation: "",
    department: "",
    officeAddress: "",
    applicationSubject: "",
    applicationPurpose: "",
    applicationDetails: "",
    legalBasis: "",
    urgency: "",
    supportingDocuments: "",
    previousApplications: "",
    requestedAction: "",
    timeframe: "",
    followUpRequired: "",
    reference: "",
    declaration: false,
  });

  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [extractedContent, setExtractedContent] = useState({});

  const handleDocumentsChange = (files, content) => {
    setUploadedDocuments(files);
    setExtractedContent(content);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const selectedCountry = formData.country || country || "";
    const selectedState = formData.state || stateVal || "";
    const selectedDistrict = formData.district || district || "";
    
    let detailsText = `
👤 Applicant Details:
Full Name: ${formData.applicantFullName}
Father's/Husband's Name: ${formData.applicantFatherOrHusband}
Age: ${formData.applicantAge}
Gender: ${formData.applicantGender}
Occupation: ${formData.applicantOccupation}
Contact: ${formData.applicantContact}
Email: ${formData.applicantEmail}
Address: ${formData.applicantAddress}

📋 Application Details:
Application Type: ${formData.applicationTypeOther || formData.applicationType}
Application To: ${formData.applicationTo}
Designation: ${formData.designation}
Department: ${formData.department}
Office Address: ${formData.officeAddress}

📝 Application Content:
Subject: ${formData.applicationSubject}
Purpose: ${formData.applicationPurpose}
Details: ${formData.applicationDetails}
Legal Basis: ${formData.legalBasis}
Requested Action: ${formData.requestedAction}

⏰ Processing Information:
Urgency: ${formData.urgency}
Timeframe: ${formData.timeframe}
Supporting Documents: ${formData.supportingDocuments}
Previous Applications: ${formData.previousApplications}
Follow-up Required: ${formData.followUpRequired}
Reference: ${formData.reference}
`.trim();

    if (uploadedDocuments.length > 0) {
      detailsText += `\n\n📄 Supporting Documents:`;
      uploadedDocuments.forEach((doc, idx) => {
        detailsText += `\n\nDocument ${idx + 1}: ${doc.name}`;
        if (extractedContent[doc.id]) {
          detailsText += `\nExtracted Content:\n${extractedContent[doc.id]}`;
        }
      });
    }

    navigate("/drafts", {
      state: {
        query: detailsText,
        summary: detailsText,
        country: selectedCountry,
        state: selectedState,
        district: selectedDistrict,
        lawCategory,
  draftType: "application",
  entityName: formData.applicantFullName,
  uploadedDocuments,
  extractedContent,
      },
    });
  };

  const renderStep1 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👤 Applicant Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Full Name *</label>
          <input
            type="text"
            name="applicantFullName"
            value={formData.applicantFullName}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Father's/Husband's Name *</label>
          <input
            type="text"
            name="applicantFatherOrHusband"
            value={formData.applicantFatherOrHusband}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Age *</label>
          <input
            type="number"
            name="applicantAge"
            value={formData.applicantAge}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Gender *</label>
          <select
            name="applicantGender"
            value={formData.applicantGender}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Occupation *</label>
          <input
            type="text"
            name="applicantOccupation"
            value={formData.applicantOccupation}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Contact Number *</label>
          <input
            type="tel"
            name="applicantContact"
            value={formData.applicantContact}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Email ID</label>
          <input
            type="email"
            name="applicantEmail"
            value={formData.applicantEmail}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-page-col">
          <label>Address *</label>
          <textarea
            name="applicantAddress"
            value={formData.applicantAddress}
            onChange={handleInputChange}
            rows={2}
            required
          />
        </div>
      </div>

      <LocationSelectors
        country={formData.country}
        state={formData.state}
        district={formData.district}
        onChange={(next) => setFormData((prev) => ({ ...prev, ...next }))}
      />
    </div>
  );

  const renderStep2 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        🏢 Application & Authority Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Application Type *</label>
          <select
            name="applicationType"
            value={formData.applicationType}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Application Type</option>
            <option value="RTI Application">RTI Application</option>
            <option value="License Application">License Application</option>
            <option value="Permission Application">Permission Application</option>
            <option value="Registration Application">Registration Application</option>
            <option value="Certificate Application">Certificate Application</option>
            <option value="Grievance Application">Grievance Application</option>
            <option value="Appeal Application">Appeal Application</option>
            <option value="Pension Application">Pension Application</option>
            <option value="Subsidy Application">Subsidy Application</option>
            <option value="Other">Other (specify)</option>
          </select>
        </div>
        <div className="form-page-col">
          {formData.applicationType === "Other" && (
            <>
              <label>Specify Application Type *</label>
              <input
                type="text"
                name="applicationTypeOther"
                value={formData.applicationTypeOther}
                onChange={handleInputChange}
                required
              />
            </>
          )}
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Application To (Authority Name) *</label>
          <input
            type="text"
            name="applicationTo"
            value={formData.applicationTo}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Designation *</label>
          <input
            type="text"
            name="designation"
            value={formData.designation}
            onChange={handleInputChange}
            placeholder="e.g., District Collector, Commissioner"
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Department/Office *</label>
          <input
            type="text"
            name="department"
            value={formData.department}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Office Address *</label>
          <textarea
            name="officeAddress"
            value={formData.officeAddress}
            onChange={handleInputChange}
            rows={2}
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        📝 Application Content
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Application Subject *</label>
          <input
            type="text"
            name="applicationSubject"
            value={formData.applicationSubject}
            onChange={handleInputChange}
            placeholder="Brief subject line for the application"
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Application Purpose *</label>
          <textarea
            name="applicationPurpose"
            value={formData.applicationPurpose}
            onChange={handleInputChange}
            rows={3}
            placeholder="Purpose and objective of this application..."
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Detailed Application Content *</label>
          <textarea
            name="applicationDetails"
            value={formData.applicationDetails}
            onChange={handleInputChange}
            rows={5}
            placeholder="Detailed description of your application, background information, and circumstances..."
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Legal Basis/Reference</label>
          <textarea
            name="legalBasis"
            value={formData.legalBasis}
            onChange={handleInputChange}
            rows={3}
            placeholder="Legal provisions, rules, or acts under which this application is made..."
          />
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <>
      <div className="form-page-section">
        <div className="form-page-section-title">
          📋 Additional Information
        </div>
        <div className="form-page-row">
          <div className="form-page-col full-width">
            <label>Requested Action *</label>
            <textarea
              name="requestedAction"
              value={formData.requestedAction}
              onChange={handleInputChange}
              rows={3}
              placeholder="Specific action you are requesting from the authority..."
              required
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Urgency Level</label>
            <select
              name="urgency"
              value={formData.urgency}
              onChange={handleInputChange}
            >
              <option value="">Select Urgency</option>
              <option value="Immediate">Immediate</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Normal">Normal</option>
            </select>
          </div>
          <div className="form-page-col">
            <label>Expected Timeframe</label>
            <input
              type="text"
              name="timeframe"
              value={formData.timeframe}
              onChange={handleInputChange}
              placeholder="e.g., 30 days, 2 weeks"
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Supporting Documents</label>
            <textarea
              name="supportingDocuments"
              value={formData.supportingDocuments}
              onChange={handleInputChange}
              rows={3}
              placeholder="List of documents attached with this application..."
            />
          </div>
          <div className="form-page-col">
            <label>Previous Applications</label>
            <textarea
              name="previousApplications"
              value={formData.previousApplications}
              onChange={handleInputChange}
              rows={3}
              placeholder="Details of any previous applications made for the same matter..."
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Follow-up Required</label>
            <select
              name="followUpRequired"
              value={formData.followUpRequired}
              onChange={handleInputChange}
            >
              <option value="">Select Follow-up</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
          <div className="form-page-col">
            <label>Reference Number</label>
            <input
              type="text"
              name="reference"
              value={formData.reference}
              onChange={handleInputChange}
              placeholder="Any reference number or file number"
            />
          </div>
        </div>
      </div>
      <div className="form-page-section">
        <div className="form-page-section-title">📄 Supporting Documents</div>
        <p className="form-page-note">
          Upload relevant documents (previous applications, receipts, identity proofs, official notices). Extracted text will be added to strengthen your application draft.
        </p>
        <DocumentUpload
          onDocumentsChange={handleDocumentsChange}
          maxFiles={8}
          acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
        />
      </div>

      <div className="form-page-declaration">
        <div className="form-page-section-title">
          ✅ Declaration
        </div>
        <label>
          <input
            type="checkbox"
            name="declaration"
            checked={formData.declaration}
            onChange={handleInputChange}
            required
          />
          <span>I declare that all the information provided in this application is true and correct *</span>
        </label>
      </div>
    </>
  );

  return (
    <div className="form-page-container">
      <div className="form-page-header">
        <a href="#" onClick={() => navigate(-1)} className="form-page-back-link">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Draft Selection
        </a>
        <h1 className="form-page-title">Create Application Document</h1>
        <p className="form-page-subtitle">Fill in the details below to generate a professional application</p>
      </div>

      <div className="form-page-content">
        <form onSubmit={handleSubmit}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}

          <div className="form-page-navigation">
            <div className="form-page-step-indicator">
              Step {currentStep} of {totalSteps}
            </div>
            <div className="form-page-buttons">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="form-page-btn form-page-btn-secondary"
                >
                  Previous
                </button>
              )}
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="form-page-btn form-page-btn-primary"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  className="form-page-btn form-page-btn-primary"
                >
                  Generate Application Document
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplicationFormPage;
